import { useCallback, useState } from 'react';
import { useOpenRegisterModal } from '@/components/RegisterModal';
import { useSubscription } from '@/features/billing/hooks/useSubscription';
import { useUpgradeModal } from '@/features/billing/hooks/useUpgradeModal';
import type {
  OrgPricingTier,
  SubscriptionType,
} from '@/features/billing/types';
import { useEventEmailSubscriptions } from '@/features/experiences/hooks/useEventEmailSubscriptions';
import { Button, useToast, VStack } from '@/ui';
import { SeatPackSelector } from './SeatPackSelector';
import { UpgradeButton } from './UpgradeButton';

const CONTACT_HREF = 'mailto:support@agendahero.com';

type Props = {
  tier: OrgPricingTier;
  subscriptionType: SubscriptionType;
};

// The action block of a pricing pitch: checkout (with the Organizations seat
// picker), contact, or register. Separate from OrgPricingCard so a lone-card
// pitch can pin it to the modal foot while the comparison table keeps it
// inside each card.
export const OrgPricingCta = ({ tier, subscriptionType }: Props) => {
  const { redirect, closeUpgradeModal } = useUpgradeModal();
  const toast = useToast();
  const { openRegisterModal } = useOpenRegisterModal();
  const { lastEmail } = useEventEmailSubscriptions();
  const { isFreeTier } = useSubscription();
  const [seatPacks, setSeatPacks] = useState(0);

  // "Current plan" / disabled only for users actually on the Free tier — Plus
  // and Family subscribers aren't on Free even though they're authenticated.
  const isCurrentFreePlan = isFreeTier && tier.name === 'Free';

  const handleAuthClick = useCallback(() => {
    closeUpgradeModal();
    openRegisterModal({
      defaults: { email: lastEmail },
      onAuthenticated: (result) => {
        const isSuccess = result === 'register' || result === 'login';
        toast.notify(
          isSuccess
            ? `Successfully ${result === 'register' ? 'registered' : 'signed in'}! 🎉`
            : 'There was an error signing in, please try again.'
        );
      },
    });
  }, [lastEmail, closeUpgradeModal, openRegisterModal, toast]);

  const buttonStyles = {
    borderRadius: 'full',
    backgroundColor: tier.buttonVariant === 'outline' ? 'white' : '#5D3FF6',
    color: tier.buttonVariant === 'outline' ? 'black' : 'white',
    borderColor: '#5D3FF6',
    isDisabled: isCurrentFreePlan,
    fontWeight: '700',
    size: 'md',
    transition: 'all 0.2s',
    variant: tier.buttonVariant,
    w: 'full',
    _hover: {
      transform: 'translateY(-1px)',
      shadow: 'md',
    },
  };

  return tier.action === 'checkout' ? (
    <VStack spacing={0} w="full">
      {tier.seatPacksSelectable && (
        <SeatPackSelector
          packs={seatPacks}
          subscriptionType={subscriptionType}
          onChange={setSeatPacks}
        />
      )}
      <UpgradeButton
        {...buttonStyles}
        packageId={tier.packageId}
        packs={tier.seatPacksSelectable ? seatPacks : undefined}
        redirect={redirect}
        subscriptionType={subscriptionType}
        variant={tier.buttonVariant}
      >
        {tier.buttonText}
      </UpgradeButton>
    </VStack>
  ) : tier.action === 'contact' ? (
    <Button
      {...buttonStyles}
      as="a"
      href={CONTACT_HREF}
      rel="noopener noreferrer"
      target="_blank"
    >
      {tier.buttonText}
    </Button>
  ) : (
    <Button {...buttonStyles} onClick={handleAuthClick}>
      {isCurrentFreePlan ? 'Your current plan' : tier.buttonText}
    </Button>
  );
};
